export const MODELS = {
  turbo: {
    label: "Whisper Large-v3 Turbo",
    repo: "onnx-community/whisper-large-v3-turbo_timestamped",
    note: "Best accuracy for Hindi/Hinglish",
    size: { gpu16: 565, gpu: 760, cpu: 1085 },
    dtype: {
      gpu16: { encoder_model: "q4f16", decoder_model_merged: "q4f16" },
      gpu: { encoder_model: "q4", decoder_model_merged: "q4" },
      cpu: { encoder_model: "q8", decoder_model_merged: "q8" },
    },
  },
  small: {
    label: "Whisper Small",
    repo: "onnx-community/whisper-small_timestamped",
    note: "Good balance, works without WebGPU",
    size: { gpu16: 322, gpu: 586, cpu: 249 },
    dtype: {
      gpu16: { encoder_model: "fp16", decoder_model_merged: "q4f16" },
      gpu: { encoder_model: "fp32", decoder_model_merged: "q4" },
      cpu: { encoder_model: "q8", decoder_model_merged: "q8" },
    },
  },
  base: {
    label: "Whisper Base",
    repo: "onnx-community/whisper-base_timestamped",
    note: "Faster, lower accuracy",
    size: { gpu16: 110, gpu: 206, cpu: 77 },
    dtype: {
      gpu16: { encoder_model: "fp16", decoder_model_merged: "q4f16" },
      gpu: { encoder_model: "fp32", decoder_model_merged: "q4" },
      cpu: { encoder_model: "q8", decoder_model_merged: "q8" },
    },
  },
  tiny: {
    label: "Whisper Tiny",
    repo: "onnx-community/whisper-tiny_timestamped",
    note: "For very weak phones, lowest accuracy",
    size: { gpu16: 62, gpu: 120, cpu: 41 },
    dtype: {
      gpu16: { encoder_model: "fp16", decoder_model_merged: "q4f16" },
      gpu: { encoder_model: "fp32", decoder_model_merged: "q4" },
      cpu: { encoder_model: "q8", decoder_model_merged: "q8" },
    },
  },
};

export const LANGUAGES = [
  { id: "auto", label: "Auto-detect", code: null },
  { id: "hi", label: "Hindi", code: "hi" },
  { id: "en", label: "English", code: "en" },
  { id: "hinglish", label: "Hinglish", code: "hi" },
];

export function modelConfig(key, device, forceCpu) {
  const m = MODELS[key];
  const useGpu = device?.webgpu && !forceCpu;
  const mode = useGpu ? (device.f16 ? "gpu16" : "gpu") : "cpu";
  return {
    key,
    label: m.label,
    model: m.repo,
    device: useGpu ? "webgpu" : "wasm",
    dtype: m.dtype[mode],
    sizeMB: m.size[mode],
    cacheKey: `${key}_${mode}`,
    modeLabel: useGpu ? "WebGPU (graphics chip)" : "CPU (WASM)",
  };
}

export async function checkDevice() {
  const r = {
    webgpu: false,
    f16: false,
    memory: navigator.deviceMemory || null,
    cores: navigator.hardwareConcurrency || null,
    mobile: /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent),
  };
  try {
    if (navigator.gpu) {
      const a = await navigator.gpu.requestAdapter();
      if (a) {
        r.webgpu = true;
        r.f16 = a.features.has("shader-f16");
      }
    }
  } catch (e) {
    r.gpuError = String(e?.message || e);
  }
  const lowMem = r.memory && r.memory <= 4;
  const bad = badModels();
  r.bad = bad;
  const mode = r.webgpu ? (r.f16 ? "gpu16" : "gpu") : "cpu";
  if (r.webgpu && !lowMem && !bad.includes(`turbo_${mode}`)) {
    r.recommended = "turbo";
    r.reason = "WebGPU is available — using Whisper Large-v3 Turbo for best accuracy.";
  } else if ((r.memory && r.memory <= 2) || bad.includes(`small_${mode}`)) {
    r.recommended = "base";
    r.reason = "Low device memory detected — Whisper Base is safer on this device.";
  } else {
    r.recommended = "small";
    r.reason = r.webgpu
      ? "WebGPU found but device memory is limited — Whisper Small chosen to avoid crashes."
      : "WebGPU is not available in this browser — falling back to Whisper Small on CPU (less accurate for Hindi).";
  }
  if (bad.length) r.reason += ` (Tuned from a previous crash on this device: ${bad.join(", ")}.)`;
  return r;
}

export const badModels = () => JSON.parse(localStorage.getItem("cc_bad_models") || "[]");

export function markBadModel(cacheKey) {
  const b = badModels();
  if (!b.includes(cacheKey)) localStorage.setItem("cc_bad_models", JSON.stringify([...b, cacheKey]));
}

export const clearBadModels = () => localStorage.removeItem("cc_bad_models");
